import { WorkloadType } from '@/components/Workload/types';
import useSWR from 'swr';
import { getDeploymentDetail, getStatefulSetDetail } from '../api';
import { getDaemonSetDetail } from '../api/daemonset.api';
import { WorkloadFetch } from '../constants';
import { useWorkloadAuth } from './useWorkloadAuth';

const { DEPLOYMENT, STATEFULSET, DAEMONSET } = WorkloadType;
interface UseWorkloadDetailProps {
  cluster: string;
  namespace: string;
  deployment: string;
}

const apiFunctionMap: Record<WorkloadType, (params: UseWorkloadDetailProps) => Promise<any>> = {
  [DEPLOYMENT]: (params: UseWorkloadDetailProps) =>
    getDeploymentDetail({
      cluster: params.cluster,
      namespace: params.namespace,
      deployment: params.deployment,
    }),
  [STATEFULSET]: (params: UseWorkloadDetailProps) =>
    getStatefulSetDetail({
      cluster: params.cluster,
      namespace: params.namespace,
      statefulset: params.deployment,
    }),
  [DAEMONSET]: async (params: UseWorkloadDetailProps) =>
    getDaemonSetDetail({
      cluster: params.cluster,
      namespace: params.namespace,
      daemonset: params.deployment,
    }),
};

export const useWorkloadDetail = (
  module: WorkloadType,
  { cluster, namespace, deployment }: UseWorkloadDetailProps,
) => {
  const { DETAIL_ACCESS } = useWorkloadAuth(module);
  const shouldFetch = !!cluster && !!namespace && !!deployment && DETAIL_ACCESS;
  const swrKey = { key: WorkloadFetch.DETAIL, module, cluster, namespace, deployment };

  const {
    data: response,
    error,
    isLoading,
    isValidating,
    mutate,
  } = useSWR(
    !shouldFetch ? null : swrKey, // 第一个参数是 swr 形参 key，只有在 shouldFetch 为 true 时才会触发请求
    fetcher,
    {
      revalidateOnFocus: false,
      refreshInterval: 15 * 1000,
    },
  );

  return {
    response,
    error,
    isLoading,
    isValidating,
    mutate,
  };
};

const fetcher = async ({
  module,
  cluster,
  namespace,
  deployment,
}: {
  module: WorkloadType;
  cluster: string;
  namespace: string;
  deployment: string;
}) => {
  // 根据 module 查找对应的 API 函数
  const fetcherFunction = apiFunctionMap[module];
  if (!fetcherFunction) {
    // 如果找不到对应的函数，回退默认请求无状态副本集
    return getDeploymentDetail({ cluster, namespace, deployment });
  }
  return fetcherFunction({ cluster, namespace, deployment });
};
